import { useEffect, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, TextField, Typography,
} from '@mui/material';
import MDButton from '@components/MDButton';
import { Form } from '@components/Form';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import {
    CustomFieldResponse, ECustomFieldType,
} from '@layouts/pages/Settings/general/custom-fields/services/responses/custom-field.response';
import {
    customFieldOptions, customFieldValueError,
} from '@layouts/pages/Settings/general/custom-fields/custom-field-value';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { CrmLeadService } from '../../kanban-board/services/crm-lead.service';
import { LeadResponse } from '../../kanban-board/services/responses/lead.response';

interface Props {
    lead: LeadResponse;
    /// Só os ativos, na ordem de exibição.
    fields: CustomFieldResponse[];
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
}

/// Edita os campos personalizados ativos; salvar manda todos e o que ficar vazio é apagado.
export const LeadCustomFieldsModal = ({ lead, fields, open, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [values, setValues] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const stored = (fieldId: string) =>
        lead.customFieldValues?.find((value) => value.customFieldId === fieldId)?.value;

    // Só ao abrir e ao trocar de lead: recarregar com o modal aberto apagaria o digitado.
    useEffect(() => {
        if (!open) return;

        setValues(Object.fromEntries(fields.map((field) => [field.id, stored(field.id) ?? ''])));
        setErrors({});
    }, [open, lead.id]);

    const change = (fieldId: string, value: string | null) => {
        setValues((prev) => ({ ...prev, [fieldId]: value ?? '' }));
        setErrors((prev) => ({ ...prev, [fieldId]: undefined }));
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
    };

    const save = async () => {
        const found = Object.fromEntries(
            fields
                .map((field) => [field.id, customFieldValueError(field, values[field.id], stored(field.id))])
                .filter(([, error]) => error)
        );

        setErrors(found);

        if (Object.keys(found).length > 0) return;

        setSaving(true);

        try {
            await new CrmLeadService().setCustomFields(
                lead.id,
                fields
                    .filter((field) => values[field.id]?.trim())
                    .map((field) => ({ customFieldId: field.id, value: values[field.id].trim() }))
            );
            showAlert(t('general.atualizacao'), 'success', 2000);
            onSaved();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    const input = (field: CustomFieldResponse) => {
        const value = values[field.id] ?? '';
        const hasError = !!errors[field.id];

        switch (field.type) {
            case ECustomFieldType.List:
                return (
                    <StandaloneAutoCompleteSelect
                        options={customFieldOptions(field, stored(field.id)).map((option) => ({
                            value: option,
                            label: option,
                        }))}
                        value={value || null}
                        onChange={(selected) => change(field.id, selected as string)}
                        isClearable={!field.required}
                    />
                );
            // Duas opções, não interruptor: "não respondido" é diferente de "não".
            case ECustomFieldType.Boolean:
                return (
                    <StandaloneAutoCompleteSelect
                        options={[
                            { value: 'true', label: t('camposPersonalizados.sim') },
                            { value: 'false', label: t('camposPersonalizados.nao') },
                        ]}
                        value={value || null}
                        onChange={(selected) => change(field.id, selected as string)}
                        isClearable={!field.required}
                    />
                );
            case ECustomFieldType.Date:
                return (
                    <TextField
                        fullWidth
                        type="date"
                        value={value}
                        onChange={(e) => change(field.id, e.target.value)}
                        error={hasError}
                        sx={inputSx}
                    />
                );
            case ECustomFieldType.Number:
                return (
                    <TextField
                        fullWidth
                        type="number"
                        value={value}
                        onChange={(e) => change(field.id, e.target.value)}
                        error={hasError}
                        sx={inputSx}
                    />
                );
            default:
                return (
                    <TextField
                        fullWidth
                        value={value}
                        onChange={(e) => change(field.id, e.target.value)}
                        error={hasError}
                        sx={inputSx}
                    />
                );
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('telaLead.editarCamposPersonalizados')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {lead.contactName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 2, pt: 2 }}>
                {fields.map((field) => (
                    <FormControl key={field.id} fullWidth>
                        <Form.Label>
                            {field.name}
                            {field.required && <Form.MandatoryIcon />}
                        </Form.Label>
                        <Box>{input(field)}</Box>
                        {errors[field.id] && (
                            <Typography color="error" variant="caption">
                                {t(errors[field.id])}
                            </Typography>
                        )}
                    </FormControl>
                ))}
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton variant="gradient" color="success" onClick={save} disabled={saving}>
                    {t('general.salvar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
